import { useMemo, useState } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { startOfISOWeek, addWeeks, addDays, format, getISOWeek, getISOWeekYear, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, CheckCircle2, Lock, Unlock, X, Clock, AlertCircle, Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useEmployees, useSchedules, useWeeklyValidation, useValidateWeek, useUnvalidateWeek,
  usePharmacySettings, isMissingSignatures, type MissingSignature,
} from '@/api/resources';
import { ApiError } from '@/api/client';
import { weekDayCount, slotDefaults, type SlotDefaults } from '@/lib/schedule';
import { isEmployedBetween } from '@/lib/employment';
import type { Schedule, ScheduleStatus } from '@/types';

// Le dimanche n'apparaît que si l'officine ouvre ce jour-là — une semaine ne
// peut pas se valider sans montrer toutes les heures qu'elle contient.
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

interface DayVal {
  status: ScheduleStatus;
  morningStart: string | null;
  morningEnd: string | null;
  afternoonStart: string | null;
  afternoonEnd: string | null;
}

const parseHM = (t?: string | null) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) + (m || 0) / 60;
};
const dayHours = (d: DayVal | undefined) => {
  if (!d || d.status !== 'WORK') return 0;
  const m = d.morningStart && d.morningEnd ? parseHM(d.morningEnd) - parseHM(d.morningStart) : 0;
  const a = d.afternoonStart && d.afternoonEnd ? parseHM(d.afternoonEnd) - parseHM(d.afternoonStart) : 0;
  return m + a;
};
const STATUS_LABEL: Record<string, string> = { WORK: '', OFF: 'Repos', LEAVE: 'Congé', SICK: 'Arrêt', ACCIDENT: 'AT', MATERNITY: 'Mat.', HOLIDAY: 'Férié' };

export default function ValidationPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const { data: pharmacy } = usePharmacySettings();
  const dayCount = weekDayCount(pharmacy?.openingHours);
  const gridCols = `180px repeat(${dayCount}, 1fr) 90px`;
  const weekStart = useMemo(() => startOfISOWeek(addWeeks(new Date(), weekOffset)), [weekOffset]);
  const weekEnd = useMemo(() => addDays(weekStart, dayCount - 1), [weekStart, dayCount]);
  const fromIso = format(weekStart, 'yyyy-MM-dd');
  const toIso = format(weekEnd, 'yyyy-MM-dd');
  const year = getISOWeekYear(weekStart);
  const week = getISOWeek(weekStart);

  const { data: employeesRaw } = useEmployees();
  // Hors contrat, pas de ligne : une semaine d'août n'a pas à demander la
  // signature de quelqu'un embauché en septembre.
  const employees = useMemo(
    () => employeesRaw?.filter((e) => isEmployedBetween(e, weekStart, weekEnd)),
    [employeesRaw, weekStart, weekEnd],
  );
  const { data: schedules } = useSchedules(fromIso, toIso);
  const { data: valData } = useWeeklyValidation(year, week);
  const validation = valData?.validation;
  const validateMut = useValidateWeek();
  const unvalidateMut = useUnvalidateWeek();

  const isValidated = validation?.status === 'VALIDATED' || validation?.status === 'FROZEN';
  const signedSet = useMemo(
    () => new Set((valData?.signatures ?? []).map((s) => s.employeeId)),
    [valData]
  );

  const [adjustments, setAdjustments] = useState<Map<string, DayVal>>(new Map());
  const [editCell, setEditCell] = useState<{ empId: string; date: string; day: DayVal } | null>(null);

  const scheduleMap = useMemo(() => {
    const map = new Map<string, Schedule>();
    schedules?.forEach((s) => map.set(`${s.employeeId}|${s.date.slice(0, 10)}`, s));
    return map;
  }, [schedules]);

  const resolve = (empId: string, dateKey: string): DayVal => {
    const adj = adjustments.get(`${empId}|${dateKey}`);
    if (adj) return adj;
    const s = scheduleMap.get(`${empId}|${dateKey}`);
    return s
      ? { status: s.status, morningStart: s.morningStart ?? null, morningEnd: s.morningEnd ?? null, afternoonStart: s.afternoonStart ?? null, afternoonEnd: s.afternoonEnd ?? null }
      : { status: 'OFF', morningStart: null, morningEnd: null, afternoonStart: null, afternoonEnd: null };
  };
  const isAdjusted = (empId: string, dateKey: string) => adjustments.has(`${empId}|${dateKey}`);

  // Salariés ayant des heures cette semaine → doivent avoir signé pour valider.
  const concernedIds = new Set<string>();
  employees?.forEach((emp) => {
    for (let i = 0; i < dayCount; i++) {
      const dk = format(addDays(weekStart, i), 'yyyy-MM-dd');
      const d = resolve(emp.id, dk);
      if (d.status === 'WORK' && dayHours(d) > 0) { concernedIds.add(emp.id); break; }
    }
  });
  // Qui est ATTENDU à la signature parmi eux. Deux personnes ne le sont pas, et
  // pour des raisons différentes :
  //   • celle qui n'a pas de compte — elle ne peut pas signer, l'attendre
  //     reviendrait à ne jamais valider la semaine ;
  //   • celle dont le titulaire a désactivé la signature sur sa fiche.
  // Le serveur applique exactement la même règle ; ici, c'est pour l'affichage.
  const isExpectedToSign = (e: { hasAccount?: boolean; signatureRequired?: boolean }) =>
    !!e.hasAccount && e.signatureRequired !== false;
  const missingEmployees = (employees ?? []).filter(
    (e) => concernedIds.has(e.id) && isExpectedToSign(e) && !signedSet.has(e.id),
  );
  const missingNames = missingEmployees.map((e) => `${e.firstName} ${e.lastName}`);
  const allSigned = missingNames.length === 0;

  function applyEdit(v: DayVal) {
    if (!editCell) return;
    setAdjustments((prev) => { const next = new Map(prev); next.set(`${editCell.empId}|${editCell.date}`, v); return next; });
    setEditCell(null);
  }

  // Signatures manquantes que le SERVEUR a refusées : c'est cette liste-là qui
  // s'affiche dans la confirmation, pas celle calculée ici. L'écran peut avoir
  // une fraction de seconde de retard (une signature qui arrive pendant qu'on
  // regarde la semaine) ; le serveur, non.
  const [pendingConfirm, setPendingConfirm] = useState<MissingSignature[] | null>(null);
  const [validateError, setValidateError] = useState<string | null>(null);

  function adjustmentsPayload() {
    return [...adjustments.entries()].map(([k, v]) => {
      const [employeeId, date] = k.split('|');
      return { employeeId, date, ...v };
    });
  }

  async function handleValidate(force = false) {
    setValidateError(null);
    try {
      await validateMut.mutateAsync({ year, week, adjustments: adjustmentsPayload(), force });
      setAdjustments(new Map());
      setPendingConfirm(null);
    } catch (err) {
      // 409 « il manque des signatures » n'est pas une panne : c'est la
      // question posée au titulaire. On la lui pose, avec les noms du serveur.
      if (err instanceof ApiError && err.status === 409 && isMissingSignatures(err.body)) {
        setPendingConfirm(err.body.missing);
        return;
      }
      setPendingConfirm(null);
      setValidateError(err instanceof Error ? err.message : 'La validation a échoué.');
    }
  }

  async function handleUnvalidate() {
    if (!window.confirm('Dévalider cette semaine ? Les heures seront déverrouillées et le planning se re-calculera depuis les disponibilités.')) return;
    await unvalidateMut.mutateAsync({ year, week });
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight">Validation des heures <PageHelp topic="validation" /></h1>
            <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
              Semaine {week} · {format(weekStart, 'd MMM', { locale: fr })} au {format(weekEnd, 'd MMM yyyy', { locale: fr })}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-4">
            <button onClick={() => setWeekOffset((o) => o - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronLeft size={16} /></button>
            <button onClick={() => setWeekOffset(0)} className="px-2 py-1 rounded-lg hover:bg-slate-100 text-[11px] font-medium text-slate-600">Cette semaine</button>
            <button onClick={() => setWeekOffset((o) => o + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {isValidated ? (
            <Button variant="default" icon={Unlock} loading={unvalidateMut.isPending} onClick={handleUnvalidate}>Dévalider</Button>
          ) : (
            <Button
              variant="primary"
              icon={CheckCircle2}
              loading={validateMut.isPending}
              title={allSigned ? undefined : `Sans la signature de : ${missingNames.join(', ')}`}
              onClick={() => handleValidate(false)}
            >
              Valider la semaine{adjustments.size > 0 ? ` (${adjustments.size} ajustement${adjustments.size > 1 ? 's' : ''})` : ''}
            </Button>
          )}
        </div>
      </div>

      {validateError && (
        <div className="bg-danger-bg border-b border-danger/30 px-8 py-2 text-[12px] text-danger flex items-center gap-2">
          <AlertCircle size={13} className="flex-shrink-0" />
          {validateError}
        </div>
      )}

      {isValidated ? (
        <div className="bg-green-50 border-b border-green-200 px-8 py-2 text-[12px] text-green-800 flex items-center gap-2">
          <Lock size={13} /> Semaine <strong>validée</strong>{validation?.validatedAt ? ` le ${new Date(validation.validatedAt).toLocaleDateString('fr-FR')}` : ''} — {validation?.totalHours ?? 0}h dont {validation?.overtimeHours ?? 0}h supp. Heures verrouillées (utilisées telles quelles dans la fiche navette).
        </div>
      ) : (
        <div className="bg-primary-bg/40 border-b border-primary/15 px-8 py-2 text-[11.5px] text-slate-600 flex items-center gap-2">
          <AlertCircle size={13} className="text-primary flex-shrink-0" />
          Le planning affiche les heures <strong>prévues</strong>. Ajustez ici les <strong>heures réellement travaillées</strong> (double-clic sur une case), puis validez : la semaine sera figée et servira de base à la paie.
        </div>
      )}

      {!isValidated && !allSigned && (
        <div className="bg-amber-50 border-b border-amber-200 px-8 py-2 text-[12px] text-amber-800 flex items-center gap-2">
          <AlertCircle size={13} className="flex-shrink-0" />
          Pas encore signée par&nbsp;: <strong>{missingNames.join(', ')}</strong>. Vous pouvez
          valider quand même — il vous sera demandé de confirmer.
        </div>
      )}

      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden min-w-[900px]">
          <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: gridCols }}>
            <div className="px-3 py-2.5 text-[11px] font-medium text-slate-600 uppercase tracking-wider border-r border-slate-200">Salarié</div>
            {DAYS.slice(0, dayCount).map((d, i) => (
              <div key={d} className="px-2 py-2.5 text-center border-r border-slate-200">
                <div className="text-[11px] font-medium">{d}</div>
                <div className="text-[10px] text-slate-500 tabular-nums">{format(addDays(weekStart, i), 'dd/MM')}</div>
              </div>
            ))}
            <div className="px-2 py-2.5 text-center text-[11px] font-medium text-slate-600 uppercase">Réel / contrat</div>
          </div>

          {employees?.map((emp, idx) => {
            let weekH = 0;
            return (
              <div key={emp.id} className={`grid border-b border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`} style={{ gridTemplateColumns: gridCols }}>
                <div className="px-3 py-2 border-r border-slate-200 flex items-center gap-2">
                  <div className="w-1 h-8 rounded-full" style={{ backgroundColor: emp.color }} />
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium truncate">{emp.firstName} {emp.lastName}</div>
                    <div className="text-[10px] text-slate-500">{emp.contractHours}h/sem</div>
                    {concernedIds.has(emp.id) && (
                      signedSet.has(emp.id) ? (
                        <div className="text-[9px] font-medium text-green-700 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-600" /> Signé
                        </div>
                      ) : isExpectedToSign(emp) ? (
                        <div className="text-[9px] font-medium text-amber-700 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Non signé
                        </div>
                      ) : (
                        // Ni vert ni ambre : il n'y a rien à attendre de lui. Le
                        // dire explicitement évite de faire passer une absence de
                        // signature pour un retard qu'on pourrait relancer.
                        <div
                          className="text-[9px] font-medium text-slate-400 flex items-center gap-1"
                          title={
                            emp.hasAccount
                              ? 'Signature désactivée sur sa fiche — sa semaine se valide sans lui.'
                              : "Pas de compte : ce salarié n'utilise pas l'application."
                          }
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                          {emp.hasAccount ? 'Signature désactivée' : 'Sans application'}
                        </div>
                      )
                    )}
                  </div>
                </div>
                {Array.from({ length: dayCount }).map((_, dayIdx) => {
                  const dateKey = format(addDays(weekStart, dayIdx), 'yyyy-MM-dd');
                  const d = resolve(emp.id, dateKey);
                  const h = dayHours(d);
                  weekH += h;
                  const adjusted = isAdjusted(emp.id, dateKey);
                  const isWork = d.status === 'WORK' && h > 0;
                  return (
                    <div key={dayIdx} className="p-1 border-r border-slate-100">
                      <button
                        onDoubleClick={() => !isValidated && setEditCell({ empId: emp.id, date: dateKey, day: d })}
                        disabled={isValidated}
                        title={isValidated ? 'Semaine verrouillée' : 'Double-clic pour ajuster le réel'}
                        className={`w-full h-full min-h-[40px] rounded border px-1.5 py-1 text-[10px] font-medium tabular-nums flex flex-col items-center justify-center gap-0.5 transition-colors
                          ${adjusted ? 'border-amber-400 bg-amber-50 text-amber-800 ring-1 ring-amber-300'
                            : isWork ? 'bg-primary-bg text-primary border-primary/25'
                            : 'bg-slate-50 text-slate-400 border-slate-200'}
                          ${!isValidated ? 'hover:ring-2 hover:ring-primary/30 cursor-pointer' : 'cursor-default'}`}
                      >
                        {isWork ? (
                          <>
                            <span>{h.toFixed(1)}h</span>
                            {adjusted && <Pencil size={9} />}
                          </>
                        ) : (
                          <span>{STATUS_LABEL[d.status] || '—'}</span>
                        )}
                      </button>
                    </div>
                  );
                })}
                <div className="px-2 flex items-center justify-center">
                  <div className="text-right">
                    <div className={`text-[13px] font-semibold tabular-nums ${weekH > emp.contractHours ? 'text-primary' : ''}`}>{weekH.toFixed(1)}h</div>
                    <div className="text-[9px] text-slate-400 tabular-nums">
                      / {emp.contractHours}h{weekH > emp.contractHours ? ` · +${(weekH - emp.contractHours).toFixed(1)}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {(!employees || employees.length === 0) && (
            <div className="px-6 py-12 text-center text-[12px] text-slate-500">Aucun salarié actif.</div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-600">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary-bg border border-primary/25" />Travail (prévu)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-400" />Ajusté (réel ≠ prévu)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-50 border border-slate-200" />Repos / absence</span>
        </div>
      </div>

      {pendingConfirm && (
        <ConfirmWithoutSignatures
          missing={pendingConfirm}
          pending={validateMut.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => handleValidate(true)}
        />
      )}

      {editCell && (
        <EditActualModal
          initial={editCell.day}
          defaults={slotDefaults(pharmacy?.openingHours, parseISO(editCell.date).getDay())}
          onClose={() => setEditCell(null)}
          onSave={applyEdit}
        />
      )}
    </div>
  );
}

/**
 * « Vous êtes sûr ? » — la seule chose qui sépare désormais une semaine non
 * signée de sa validation. Elle NOMME les salariés : « des signatures
 * manquent » ne donne aucune prise, on ne sait ni qui relancer ni si c'est
 * normal. Les noms viennent du serveur (voir `handleValidate`).
 */
function ConfirmWithoutSignatures({ missing, pending, onCancel, onConfirm }: {
  missing: MissingSignature[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const plural = missing.length > 1;
  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-[460px]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[16px] font-semibold flex items-center gap-2">
            <AlertCircle size={16} className="text-amber-600" />
            Valider sans toutes les signatures ?
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-[13px] text-slate-700">
            {plural ? "Ces salariés n'ont pas" : "Ce salarié n'a pas"} signé la semaine dans
            l'application&nbsp;:
          </div>
          <ul className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-1">
            {missing.map((m) => (
              <li key={m.id} className="text-[12.5px] font-medium text-amber-900 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {m.firstName} {m.lastName}
              </li>
            ))}
          </ul>
          <div className="text-[11.5px] text-slate-500 leading-relaxed">
            En validant, la semaine est <strong>figée</strong> et part telle quelle dans la fiche
            navette. {plural ? 'Leur' : 'Son'} absence de signature reste attachée à la validation.
            Si {plural ? "ces salariés n'utilisent" : "ce salarié n'utilise"} jamais l'application,
            désactivez la signature sur {plural ? 'leur fiche' : 'sa fiche'} : la question ne
            reviendra plus.
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <Button variant="default" onClick={onCancel}>Annuler</Button>
          <Button variant="primary" icon={CheckCircle2} loading={pending} onClick={onConfirm}>
            Valider quand même
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditActualModal({ initial, defaults, onClose, onSave }: {
  initial: DayVal;
  /** Horaires proposés pour ce jour : ceux de l'officine, pas des valeurs en dur. */
  defaults: SlotDefaults;
  onClose: () => void;
  onSave: (v: DayVal) => void;
}) {
  const [status, setStatus] = useState<ScheduleStatus>(initial.status);
  const [hasMorning, setHasMorning] = useState(!!initial.morningStart);
  const [hasAfternoon, setHasAfternoon] = useState(!!initial.afternoonStart);
  const [mStart, setMStart] = useState(initial.morningStart ?? defaults.morningStart);
  const [mEnd, setMEnd] = useState(initial.morningEnd ?? defaults.morningEnd);
  const [aStart, setAStart] = useState(initial.afternoonStart ?? defaults.afternoonStart);
  const [aEnd, setAEnd] = useState(initial.afternoonEnd ?? defaults.afternoonEnd);

  function save() {
    if (status === 'WORK') {
      onSave({
        status: 'WORK',
        morningStart: hasMorning ? mStart : null,
        morningEnd: hasMorning ? mEnd : null,
        afternoonStart: hasAfternoon ? aStart : null,
        afternoonEnd: hasAfternoon ? aEnd : null,
      });
    } else {
      onSave({ status, morningStart: null, morningEnd: null, afternoonStart: null, afternoonEnd: null });
    }
  }

  const total = (status === 'WORK' && hasMorning ? parseHM(mEnd) - parseHM(mStart) : 0) + (status === 'WORK' && hasAfternoon ? parseHM(aEnd) - parseHM(aStart) : 0);

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-[460px]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[16px] font-semibold">Heures réellement travaillées</div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="label">Statut réel du jour</div>
            <div className="grid grid-cols-4 gap-1.5">
              {(['WORK', 'OFF', 'LEAVE', 'SICK'] as ScheduleStatus[]).map((s) => {
                const labels: Record<string, string> = { WORK: 'Travail', OFF: 'Repos', LEAVE: 'Congé', SICK: 'Arrêt' };
                return (
                  <button key={s} type="button" onClick={() => setStatus(s)}
                    className={`py-2 rounded-lg border text-[12px] font-medium ${status === s ? 'bg-primary text-white border-primary' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                    {labels[s]}
                  </button>
                );
              })}
            </div>
          </div>
          {status === 'WORK' && (
            <>
              <div>
                <label className="flex items-center gap-2 text-[11.5px] font-medium text-slate-700 mb-1.5">
                  <input type="checkbox" checked={hasMorning} onChange={(e) => setHasMorning(e.target.checked)} className="rounded" /> Matin
                </label>
                {hasMorning && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="time" value={mStart} onChange={(e) => setMStart(e.target.value)} className="input tabular-nums text-center" />
                    <input type="time" value={mEnd} onChange={(e) => setMEnd(e.target.value)} className="input tabular-nums text-center" />
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 text-[11.5px] font-medium text-slate-700 mb-1.5">
                  <input type="checkbox" checked={hasAfternoon} onChange={(e) => setHasAfternoon(e.target.checked)} className="rounded" /> Après-midi
                </label>
                {hasAfternoon && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="time" value={aStart} onChange={(e) => setAStart(e.target.value)} className="input tabular-nums text-center" />
                    <input type="time" value={aEnd} onChange={(e) => setAEnd(e.target.value)} className="input tabular-nums text-center" />
                  </div>
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11.5px] text-slate-600 flex items-center gap-2">
                <Clock size={12} /> Total réel : <strong className="text-slate-900 tabular-nums">{total.toFixed(1)}h</strong>
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <Button variant="default" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save}>Enregistrer l'ajustement</Button>
        </div>
      </div>
    </div>
  );
}
